import styles from './ScenarioThree.module.scss';
import ChartWrapper from 'components/chart-wrapper/ChartWrapper';
import ContentContainer from 'components/content-container/ContentContainer';
import InfiniteTable from 'components/infinite-table/InfiniteTable';
import { detailedViewTable } from 'constants/scenarioThree';
import ReactECharts from 'echarts-for-react';
import { useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectFiltersAllValues,
    selectMarketValue,
    selectRmValue,
    selectScenarioThreeOutlierData,
    selectScenarioThreeOutlierPage,
    selectScenarioThreeTrendData,
    selectScenarioThreeVcData,
    selectTeamValue,
    selectTimeslicerEndMonth,
    selectTimeslicerStartMonth,
    selectZoneValue
} from 'selectors/selectors';
import {
    fetchOutliersData,
    fetchTrendData,
    fetchVcData,
    switchOutlierChartPage
} from 'slices/ScenarioThreeSlice';
import { ChartType, Scenarios } from 'types/types';
import { generateRequestParamsForScenarioApi } from 'utils/api';
import {
    scenarioThreeOutlierChartPartOneOptions,
    scenarioThreeNoResultOutlierChartPartOneOptions,
    scenarioThreeOutlierChartPartTwoOptions,
    scenarioThreeTrendAnalysisChartOptions,
    scenarioThreeVcDataChartOptions
} from 'utils/charts/index';
import { areAllFilterValuesNumberValues } from 'utils/filters';

const ScenarioThree = () => {
    const dispatch = useDispatch();

    const [selectedOutlier, setSelectedOutlier] = useState(null);
    // Selectors
    const tsStart = useSelector(selectTimeslicerStartMonth) || {};
    const tsEnd = useSelector(selectTimeslicerEndMonth) || {};

    const zoneValue = useSelector(selectZoneValue);
    const marketValue = useSelector(selectMarketValue);
    const teamValue = useSelector(selectTeamValue);
    const rmValue = useSelector(selectRmValue);

    const outlierPage = useSelector(selectScenarioThreeOutlierPage);
    const outlierData = useSelector(selectScenarioThreeOutlierData);
    const vcData = useSelector(selectScenarioThreeVcData);
    const trendData = useSelector(selectScenarioThreeTrendData);
    const allValues = useSelector(selectFiltersAllValues);

    const allFilterValuesAreNotNull = (startMonth, endMonth) =>
        areAllFilterValuesNumberValues({
            startMonth,
            endMonth,
            zone: zoneValue,
            market: marketValue,
            team: teamValue,
            rm: rmValue
        });
    const getOutliersData = (startMonth, endMonth) =>
        dispatch(
            fetchOutliersData(
                generateRequestParamsForScenarioApi(
                    ChartType.Outliers,
                    Scenarios.Three,
                    marketValue,
                    teamValue,
                    rmValue,
                    startMonth,
                    endMonth,
                    allValues
                )
            )
        );

    const getVcData = (startMonth, endMonth) =>
        dispatch(
            fetchVcData(
                generateRequestParamsForScenarioApi(
                    ChartType.VC,
                    Scenarios.Three,
                    marketValue,
                    teamValue,
                    rmValue,
                    startMonth,
                    endMonth
                )
            )
        );

    const getTrendData = (startMonth, endMonth) =>
        dispatch(
            fetchTrendData(
                generateRequestParamsForScenarioApi(
                    ChartType.Trend,
                    Scenarios.Three,
                    marketValue,
                    teamValue,
                    rmValue,
                    startMonth,
                    endMonth
                )
            )
        );

    useEffect(() => {
        if (tsStart && tsEnd) {
            if (allFilterValuesAreNotNull(tsStart.epoch, tsEnd.epoch)) {
                getOutliersData(tsStart.epoch, tsEnd.epoch);
                getVcData(tsStart.epoch, tsEnd.epoch);
                getTrendData(tsStart.epoch, tsEnd.epoch);
            }
        }

        return () => {
            dispatch(switchOutlierChartPage(1));
        };
    }, [marketValue, teamValue, rmValue, tsStart, tsEnd]);

    const onChartNodeClick = (param) => {
        const { componentType, data } = param;
        if (componentType === 'series' && outlierPage === 1) {
            const clickedOutlier = outlierData.outliers.find(
                (o) => o.rmEmployeeId === data.name
            );
            setSelectedOutlier(clickedOutlier);
            componentType === 'series' && dispatch(switchOutlierChartPage(2));
        }
        if (componentType === 'graphic' && outlierPage === 2) {
            dispatch(switchOutlierChartPage(1));
        }
    };

    const { min, max, lower, upper } = { ...outlierData };
    const stats = { min, max, lower, upper };
    const { scenarioThreeTrendAnalysisChartData } = { ...trendData };
    const { data } = { ...scenarioThreeTrendAnalysisChartData };

    const onEvents = {
        click: onChartNodeClick
    };
    const memoizedOutlierChart = useMemo(() => {
        return (
            <ReactECharts
                option={
                    outlierPage === 1
                        ? outlierData &&
                          outlierData.outliers &&
                          outlierData.outliers.length > 0
                            ? scenarioThreeOutlierChartPartOneOptions(
                                  outlierData
                              )
                            : scenarioThreeNoResultOutlierChartPartOneOptions()
                        : scenarioThreeOutlierChartPartTwoOptions(
                              stats,
                              selectedOutlier
                          )
                }
                autoResize={false}
                style={{ width: '100%', height: '100%' }}
                onEvents={onEvents}
            ></ReactECharts>
        );
    }, [outlierPage, outlierData]);

    const chartRef = useRef(null);
    useEffect(() => {
        chartRef.current.getEchartsInstance().dispose();
        chartRef.current
            .getEchartsInstance()
            .setOption(scenarioThreeTrendAnalysisChartOptions(data));
    }, [data]);

    const vcChartRef = useRef(null);
    useEffect(() => {
        vcChartRef.current.getEchartsInstance().dispose();
        vcChartRef.current
            .getEchartsInstance()
            .setOption(scenarioThreeVcDataChartOptions(vcData));
    }, [vcData]);

    return (
        <ContentContainer>
            <div className={styles.chartsContainer}>
                <ChartWrapper comboClass={styles.rightMargin}>
                    {memoizedOutlierChart}
                </ChartWrapper>

                <ChartWrapper comboClass={styles.rightMargin}>
                    <ReactECharts
                        option={scenarioThreeVcDataChartOptions(vcData)}
                        style={{ width: '100%', height: '100%' }}
                        ref={vcChartRef}
                    ></ReactECharts>
                </ChartWrapper>

                <ChartWrapper>
                    <ReactECharts
                        option={scenarioThreeTrendAnalysisChartOptions(data)}
                        style={{ width: '100%', height: '100%' }}
                        ref={chartRef}
                    ></ReactECharts>
                </ChartWrapper>
            </div>
            <div className={styles.tableContainer}>
                <div className={styles.tableTitle}>Detailed View</div>
                <InfiniteTable
                    zone={zoneValue}
                    market={marketValue}
                    team={teamValue}
                    rm={rmValue}
                    start={tsStart}
                    end={tsEnd}
                    allValues={allValues}
                    columns={detailedViewTable}
                    scenario={Scenarios.Three}
                />
            </div>
        </ContentContainer>
    );
};

export default ScenarioThree;
